/**
 * 数字人视频状态机 (Task 12)
 *
 * 状态衔接 ai.stream.* 事件:
 *   - visitor.message / ai.stream.start  -> idle -> listening
 *   - 首个 ai.stream.delta (本批次临时,Task 11 落地后改 TTS chunk) -> listening -> speaking
 *   - ai.stream.end (后跟 1.2s 模拟音频尾音) -> -> idle
 *
 * 视觉降级 (spec 开放问题 #2 临时实现):见 components/DigitalHumanVideo.tsx
 *   getVideoForState 缺素材时优先 idle 兜底。
 */

export type VideoState = 'idle' | 'listening' | 'speaking' | 'greeting';

export class VideoStateMachine {
  private state: VideoState;
  private listeners: Array<(s: VideoState) => void> = [];
  private endTimer: ReturnType<typeof setTimeout> | null = null;
  /** 仅测试可注入,默认 1200ms */
  public endDelayMs: number;

  constructor(initial: VideoState = 'idle', endDelayMs = 1200) {
    this.state = initial;
    this.endDelayMs = endDelayMs;
  }

  on(fn: (s: VideoState) => void): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter((x) => x !== fn);
    };
  }

  current(): VideoState {
    return this.state;
  }

  private set(next: VideoState): void {
    if (this.state === next) return;
    this.state = next;
    this.listeners.forEach((fn) => fn(next));
  }

  /** 收到访客发消息 / ai.stream.start: -> listening */
  streamStart(): void {
    this.cancelEndTimer();
    this.set('listening');
  }

  /**
   * 首个 TTS chunk 到达 (Task 11);本批次临时:首个 ai.stream.delta。
   * 仅当当前是 listening 时才切到 speaking — 防止重复触发或乱序。
   * 用于「无语音 web 式」分支(audioEnabled=false)。
   */
  ttsChunk(): void {
    if (this.state === 'listening') this.set('speaking');
  }

  /**
   * 真音频开始播放(audioEnabled=true 路径):无条件进入 speaking,并取消任何 pending 的
   * end/降级定时器。区别于 ttsChunk:不要求当前是 listening —— 整段 TTS 合成可能慢于 5s 降级,
   * 或事件乱序导致 state 已被切走,只要音频真在播,视频就必须口播。
   */
  audioStart(): void {
    this.cancelEndTimer();
    this.set('speaking');
  }

  /**
   * 流结束。真场景下 stream.end 不应该立即切 idle (TTS 可能还在播);
   * 本批次 TTS 没做,加 endDelayMs 延迟模拟"音频还在播",然后回 idle。
   */
  streamEnd(): void {
    this.cancelEndTimer();
    this.endTimer = setTimeout(() => {
      this.endTimer = null;
      this.set('idle');
    }, this.endDelayMs);
  }

  /** 真音频结束事件 (Task 11 接 TTS 后会用) */
  audioEnded(): void {
    this.cancelEndTimer();
    this.set('idle');
  }

  /** 重置 (访客重置会话/关闭面板) */
  reset(): void {
    this.cancelEndTimer();
    this.set('idle');
  }

  private cancelEndTimer(): void {
    if (this.endTimer !== null) {
      clearTimeout(this.endTimer);
      this.endTimer = null;
    }
  }
}
